import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import Cookies from "js-cookie";
import useUserStore from "../store/user";
import { assertApiEnvelopeSuccess } from "../utils/apiEnvelope";

const uuidPattern =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Chamadas de autenticação: um 401 aqui significa "senha errada", não "sessão
 * vencida". Redirecionar nesse caso jogaria o usuário para a tela onde ele já
 * está, apagando o erro que ele precisa ler.
 */
const ehChamadaDeLogin = (error: unknown) => {
  const url = (error as { config?: { url?: string } })?.config?.url || "";
  return url.includes("/auth");
};

const createCorrelationId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;

class AxiosService {
  axiosInstance: AxiosInstance;

  multiPartFormDataConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_APP_URL_BASE,
    headers: {
      "Content-Type": "application/json",
    },
  };

  constructor() {
    const axiosConfig: AxiosRequestConfig = {
      baseURL: import.meta.env.VITE_APP_URL_BASE,
      headers: {},
      // withCredentials: true,
    };

    this.axiosInstance = axios.create(axiosConfig);

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (!config.url) {
          return config;
        }

        const urlPath = this.getUrlPath(config.url);
        if (config.headers) {
          config.headers["x-correlation-id"] =
            (config.headers["x-correlation-id"] as string | undefined) ||
            createCorrelationId();

          const profilePublicId = config.url.match(uuidPattern)?.[0];
          if (profilePublicId) {
            config.headers["x-profile-public-id"] = profilePublicId;
          }
        }

        const excludedRoutes = ["/auth/", "/imports-manager/oauth/google"];

        const isExcluded = excludedRoutes.some((route) =>
          urlPath.startsWith(route),
        );

        if (!isExcluded) {
          // Adicionar XSRF-TOKEN
          const xsrfToken = Cookies.get("XSRF-TOKEN");
          if (xsrfToken && config.headers) {
            config.headers["X-XSRF-TOKEN"] = xsrfToken;
          }

          // Adicionar Authorization Bearer Token
          const { user } = useUserStore.getState();
          if (user?.token && config.headers) {
            config.headers["Authorization"] = `Bearer ${user.token}`;
          }
        } else {
          console.debug(`Interceptor ignorando a rota: ${urlPath}`);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        assertApiEnvelopeSuccess(response.data);
        return response;
      },
      (error) => {
        // Sessão vencida: derruba e manda para o login.
        //
        // Até 07/08/2026 isto não existia porque não podia existir: o backend
        // respondia 200 com {"error":true} no corpo mesmo com o token expirado,
        // então não havia 401 para reagir. O usuário via "Erro inesperado ao
        // obter perfis em acompanhamento" e não tinha como adivinhar que
        // precisava sair e entrar. O backend passou a responder 401
        // (AuthenticationFilter); esta é a outra metade da correção.
        if (error?.response?.status === 401 && !ehChamadaDeLogin(error)) {
          useUserStore.getState().clearUser();
          Cookies.remove("token");
          // replace, não push: a tela expirada não deve voltar no "voltar".
          if (!window.location.pathname.startsWith("/login")) {
            window.location.replace("/login?sessao=expirada");
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private getUrlPath(url: string): string {
    try {
      const fullUrl = new URL(url, import.meta.env.VITE_APP_URL_BASE);
      return fullUrl.pathname;
    } catch {
      return url;
    }
  }

  /**
   * Métodos HTTP
   *
   * O `any` no retorno e divida de tipagem preexistente: trocar por
   * AxiosResponse quebra sete arquivos de teste que usam mocks parciais.
   * Fora do escopo desta mudanca — as diretivas abaixo apenas registram isso
   * em vez de esconder.
   */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get(path: string, config?: AxiosRequestConfig): Promise<any> {
    return this.axiosInstance.get(path, config);
  }

  async post(
    path: string,
    params?: unknown,
    config?: AxiosRequestConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    return this.axiosInstance.post(path, params, config);
  }

  async postT<Body, Result>(
    path: string,
    params?: Body,
    customConfig?: AxiosRequestConfig,
  ): Promise<Result> {
    const response = await this.axiosInstance.post(path, params, customConfig);
    return response.data;
  }

  async put(
    path: string,
    params: unknown,
    config?: AxiosRequestConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    return this.axiosInstance.put(path, params, config);
  }

  async putT<Body, Result>(path: string, params: Body): Promise<Result> {
    const response = await this.axiosInstance.put(path, params);
    return response.data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete(path: string, params?: unknown): Promise<any> {
    return this.axiosInstance.delete(path, { data: params });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async patch(path: string, params: unknown): Promise<any> {
    return this.axiosInstance.patch(path, params);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async head(path: string, params?: unknown): Promise<any> {
    return this.axiosInstance.head(path, { params });
  }

  /**
   * Upload de arquivos
   */
  async uploadFile(
    url: string,
    data: FormData,
    customConfig?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const customAxiosRequestConfig =
      customConfig || this.multiPartFormDataConfig;
    return this.axiosInstance.post(url, data, customAxiosRequestConfig);
  }
}

const ApiService: AxiosService = new AxiosService();

export { ApiService };
